/**
 * Reading sequences the reader typed or pasted, and nothing else (P29.5, P29.6).
 *
 * **Pure, no DOM, no `api`** — the `marks.ts` and `trajectory.ts` pattern, so the CSV rules can be
 * tested without a panel and `boundary.test.ts`'s import rule is not strained.
 *
 * ## What a "sequence" is here
 *
 * Hyphen-separated residue codes, which is the form `peptide_vae.data.sequences.split` reads and
 * the form every sequence in a tile already has (`V13`). At `V = 1,209` the codes are Titlecase
 * triples or `X####`, so `Gly-Ser-Lys` is three residues and `G-S-K` is three *different* residues
 * that this alphabet does not contain — the single most likely mistake for someone used to the
 * canonical twenty, and the one `server.py:404` exists to catch.
 *
 * ## Validation here is a courtesy, and the server is the authority
 *
 * `looksKnown` below tests a code against the **depiction library's** list, which is not the same
 * set as the checkpoint's alphabet — see `api.residueCodes`. So nothing in this module ever
 * *refuses* a code: it reports which codes are unlisted, and the caller shows that as information.
 * The one thing that is refused is a line with no codes at all, which cannot become a request.
 *
 * This is the inverse of the usual rule and it is deliberate. `§0.5` of the vis contracts says the
 * UI greys out what the server would refuse; the corollary is that it must **not** grey out what
 * the server would accept, and a list built from a different artefact cannot tell the difference.
 */

/** Split a hyphenated sequence into codes. `''` is no codes, not one empty code. */
export const codesOf = (sequence: string): string[] =>
  sequence === '' ? [] : sequence.split('-').filter((code) => code !== '');

/** Join codes back into the wire form. The inverse of `codesOf` for any well-formed input. */
export const joinCodes = (codes: readonly string[]): string => codes.join('-');

/** One row a paste produced, with the line it came from so a report can point at it. */
export interface ParsedRow {
  /** 1-based, counting every line of the input including ones that were skipped. */
  readonly line: number;
  readonly sequence: string;
  readonly label: string;
}

export interface ParseReport {
  readonly rows: readonly ParsedRow[];
  /** Lines that held something but no codes. Reported, never silently dropped. */
  readonly empty: readonly number[];
  /** Whether a header row was detected and consumed. */
  readonly header: boolean;
  /** Columns present in a headered paste that this app ignores, named so the reader knows. */
  readonly ignoredColumns: readonly string[];
}

/**
 * Split one CSV line, honouring `"` quoting.
 *
 * Small and local rather than a dependency: the input is a paste of two columns, and a full CSV
 * parser would be more bytes than this whole module for quoting rules nobody will use here.
 * Doubled quotes inside a quoted field are one quote, which is the one rule a spreadsheet export
 * actually relies on.
 */
export function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      fields.push(field);
      field = '';
    } else field += ch;
  }
  fields.push(field);
  return fields.map((f) => f.trim());
}

/** Header names this app reads, lowercased. Anything else in a headered paste is ignored. */
const SEQUENCE_HEADERS = new Set(['sequence', 'peptide', 'seq']);
const LABEL_HEADERS = new Set(['label', 'name', 'id']);

/**
 * Whether the first row is a header.
 *
 * **Decided by asking whether field 1 is a sequence, not by looking for the word `sequence`.**
 * A paste is far more likely to carry an unexpected header spelling than a peptide named
 * `sequence`, and the test cannot misfire in the direction that matters: `sequence` is not a
 * hyphenated list of residue codes, so a real header is never read as data.
 *
 * `isCode` is passed in rather than imported, because "is this a residue code" is a question about
 * the loaded atlas's library and this module holds no state.
 */
function looksLikeHeader(fields: readonly string[], isCode: (code: string) => boolean): boolean {
  const first = fields[0] ?? '';
  const codes = codesOf(first);
  if (codes.length === 0) return true;
  // A single unlisted word — `sequence`, `peptide_id` — is a header. A single *listed* code is a
  // one-residue peptide, which is unusual but legal, so it is data.
  if (codes.length === 1) return !isCode(codes[0] as string);
  return false;
}

/**
 * Read a pasted block into rows.
 *
 * Three input shapes, all accepted, because all three are things people actually paste:
 *
 * | shape | example |
 * |---|---|
 * | one sequence per line | `Leu-Val-Ala` |
 * | CSV with a header | `sequence,label` then `Leu-Val-Ala,thanatin 12` |
 * | CSV without one | `Leu-Val-Ala,thanatin 12` |
 *
 * **A second column is a label whether or not a header named it.** With a header, the label column
 * is found by name; without, it is field 2. Anything past the columns this app reads is listed in
 * `ignoredColumns` rather than dropped in silence — a reader who pasted a spreadsheet with an MIC
 * column should be told it went nowhere.
 */
export function parsePaste(text: string, isCode: (code: string) => boolean): ParseReport {
  const lines = text.split(/\r?\n/);
  const rows: ParsedRow[] = [];
  const empty: number[] = [];
  let header = false;
  let ignoredColumns: string[] = [];
  let sequenceAt = 0;
  let labelAt = 1;

  let start = 0;
  // The first line that holds anything at all is the one the header test applies to; leading blank
  // lines in a paste are common and say nothing.
  while (start < lines.length && (lines[start] as string).trim() === '') start++;

  if (start < lines.length) {
    const fields = splitCsvLine(lines[start] as string);
    if (looksLikeHeader(fields, isCode)) {
      header = true;
      const lower = fields.map((f) => f.toLowerCase());
      const foundSequence = lower.findIndex((f) => SEQUENCE_HEADERS.has(f));
      const foundLabel = lower.findIndex((f) => LABEL_HEADERS.has(f));
      // A header whose first column is not a name this app knows is still a header — the
      // `looksLikeHeader` test already established the row is not data. Falling back to column 0
      // is what makes `peptide_sequence,notes` work without enumerating every spelling.
      sequenceAt = foundSequence === -1 ? 0 : foundSequence;
      labelAt = foundLabel === -1 ? -1 : foundLabel;
      ignoredColumns = fields.filter((_, i) => i !== sequenceAt && i !== labelAt && fields[i] !== '');
      start++;
    }
  }

  for (let i = start; i < lines.length; i++) {
    const raw = lines[i] as string;
    if (raw.trim() === '') continue;
    const fields = splitCsvLine(raw);
    const sequence = joinCodes(codesOf(fields[sequenceAt] ?? ''));
    if (sequence === '') {
      empty.push(i + 1);
      continue;
    }
    rows.push({ line: i + 1, sequence, label: labelAt === -1 ? '' : (fields[labelAt] ?? '') });
  }
  return { rows, empty, header, ignoredColumns };
}

/**
 * Prefix completions for a partly typed code.
 *
 * Case-insensitive because the codes are Titlecase triples and nobody holds the shift key while
 * scanning an alphabet. **Ordered by where the match falls**: a code that *starts* with what was
 * typed comes before one that merely contains it, so typing `gly` offers `Gly` first rather than
 * `Hydroxy-Gly` on an alphabetical sort.
 */
export function completionsFor(
  typed: string,
  codes: readonly string[],
  limit: number,
): { readonly shown: string[]; readonly total: number } {
  const needle = typed.trim().toLowerCase();
  if (needle === '') return { shown: [], total: 0 };
  const starts: string[] = [];
  const contains: string[] = [];
  for (const code of codes) {
    const at = code.toLowerCase().indexOf(needle);
    if (at === 0) starts.push(code);
    else if (at > 0) contains.push(code);
  }
  const all = [...starts, ...contains];
  return { shown: all.slice(0, limit), total: all.length };
}
